import React, { useState, Fragment } from 'react';
import { Crown, Check, CheckCircle2, ShieldCheck, LayoutGrid, Target, Megaphone, Zap, TrendingUp, ShoppingBag, X, ArrowRight, Clock, Eye, MousePointerClick, DollarSign, ChevronDown, ChevronUp, RotateCcw, CalendarPlus, Rocket, ArrowUpCircle, Mail, Sparkles, BarChart3, Wallet, AlertCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface PromotionTabProps {
  showToast: (msg: string) => void;
  setActiveAutoService: (service: string | null) => void;
  setActiveTab?: (tab: string) => void;
}
interface AdOrder {
  id: string;
  type: 'banner' | 'email' | 'pin';
  name: string;
  status: 'active' | 'completed' | 'pending';
  startDate: string;
  endDate: string | null;
  price: number;
  views: number;
  clicks: number;
  orders: number;
  income: number;
}
export function PromotionTab({
  showToast,
  setActiveAutoService,
  setActiveTab
}: PromotionTabProps) {
  // State
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<'pro' | 'top' | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedPinCategory, setSelectedPinCategory] = useState<string>('');
  const [selectedPinDuration, setSelectedPinDuration] = useState<'1week' | '2weeks' | '1month'>('1week');
  const [showNewAdModal, setShowNewAdModal] = useState(false);
  const [selectedAdSlot, setSelectedAdSlot] = useState<string | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  // Mock Data
  const [adOrders, setAdOrders] = useState<AdOrder[]>([{
    id: 'ORD-A-001',
    type: 'banner',
    name: 'Баннер на главной',
    status: 'active',
    startDate: '01.03.2026',
    endDate: '07.03.2026',
    price: 5000,
    views: 12400,
    clicks: 620,
    orders: 18,
    income: 54000
  }, {
    id: 'ORD-A-002',
    type: 'email',
    name: 'Рассылка по базе',
    status: 'completed',
    startDate: '15.02.2026',
    endDate: '15.02.2026',
    price: 3000,
    views: 10200,
    clicks: 408,
    orders: 12,
    income: 36000
  }, {
    id: 'ORD-A-003',
    type: 'pin',
    name: 'Закреп в каталоге',
    status: 'active',
    startDate: '01.03.2026',
    endDate: '14.03.2026',
    price: 4000,
    views: 8900,
    clicks: 356,
    orders: 9,
    income: 27000
  }, {
    id: 'ORD-A-004',
    type: 'banner',
    name: 'Баннер в каталоге',
    status: 'pending',
    startDate: '10.03.2026',
    endDate: '17.03.2026',
    price: 3500,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0
  }]);
  const activePlacements = [{
    type: 'Закреп в каталоге',
    category: 'Натальная карта',
    daysLeft: 5,
    views: 1247
  }, {
    type: 'Баннер на главной',
    category: 'Все услуги',
    daysLeft: 3,
    views: 4200
  }];
  // Computed
  const totalAdIncome = adOrders.reduce((sum, o) => sum + o.income, 0);
  const totalAdOrders = adOrders.reduce((sum, o) => sum + o.orders, 0);
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'banner':
        return 'Баннер';
      case 'email':
        return 'Рассылка';
      case 'pin':
        return 'Закреп';
      default:
        return type;
    }
  };
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Активен',
          variant: 'success' as const
        };
      case 'completed':
        return {
          label: 'Завершен',
          variant: 'neutral' as const
        };
      case 'pending':
        return {
          label: 'Ожидание',
          variant: 'warning' as const
        };
      default:
        return {
          label: status,
          variant: 'default' as const
        };
    }
  };
  const handleRepeatOrder = (order: AdOrder) => {
    showToast(`Повторный заказ "${order.name}" создан`);
    console.log(`[Admin Sync] Ad order repeated: ${order.id}`);
  };
  const handleExtendOrder = (order: AdOrder) => {
    showToast(`Продление "${order.name}" оформлено`);
    console.log(`[Admin Sync] Ad order extended: ${order.id}`);
  };
  const handleUpgradeTariff = (tariff: 'pro' | 'top') => {
    setShowTariffModal(false);
    showToast(`Тариф ${tariff === 'pro' ? 'PRO' : 'TOP'} успешно активирован!`);
    console.log(`[Admin Sync] Tariff upgraded to ${tariff}`);
  };
  const handleBuyPin = () => {
    setShowPinModal(false);
    const prices = {
      '1week': 2000,
      '2weeks': 3500,
      '1month': 6000
    };
    showToast(`Закреп "${selectedPinCategory}" оплачен (${prices[selectedPinDuration].toLocaleString('ru-RU')} ₽)`);
    console.log(`[Admin Sync] Pin purchased: ${selectedPinCategory}, duration: ${selectedPinDuration}`);
  };
  return <div className="space-y-6" data-id="element-14113">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2" data-id="element-14114">
        <div data-id="element-14115">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-14116">
            Продвижение профиля
          </h2>
          <p className="text-slate-500 text-sm mt-1" data-id="element-14117">
            Увеличивайте заказы и доход
          </p>
        </div>
      </div>

      {/* Ad Budget */}
      <Card padding="md" className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-indigo-50/30" data-id="element-14118">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-14119">
          <div data-id="element-14120">
            <div className="flex items-center gap-2 mb-1" data-id="element-14121">
              <Wallet className="w-5 h-5 text-purple-600" data-id="element-14122" />
              <h3 className="font-bold text-purple-900" data-id="element-14123">Рекламный бюджет</h3>
            </div>
            <div className="flex items-baseline gap-3" data-id="element-14124">
              <span className="text-3xl font-bold text-purple-900" data-id="element-14125">
                4 500 ₽
              </span>
              <span className="text-sm text-purple-600" data-id="element-14126">доступно</span>
            </div>
            <div className="flex gap-4 mt-1 text-xs text-purple-700" data-id="element-14127">
              <span data-id="element-14128">
                Потрачено за месяц: <strong data-id="element-14129">3 500 ₽</strong>
              </span>
              <span data-id="element-14130">
                Остаток: <strong data-id="element-14131">4 500 ₽</strong>
              </span>
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto" data-id="element-14132">
            <Button variant="primary" size="sm" className="flex-1 sm:flex-none bg-purple-600 hover:bg-purple-700" onClick={() => showToast('Баланс пополнен на 5 000 ₽ ✓')} data-id="element-14133">
              Пополнить баланс
            </Button>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-purple-100 text-xs text-purple-600" data-id="element-14134">
          Реклама списывается с рекламного бюджета. Если средств не хватает —
          пополните баланс.
        </div>
      </Card>

      {/* Section 1: Current Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-14135">
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => showToast('Текущий тариф: Базовый')} data-id="element-14136">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14137">Тариф</div>
          <div className="text-lg font-bold text-slate-900 flex items-center gap-1.5" data-id="element-14138">
            Базовый <Crown className="w-4 h-4 text-amber-400" data-id="element-14139" />
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('Позиция #47 в каталоге');
      }} data-id="element-14140">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14141">Позиция</div>
          <div className="text-lg font-bold text-slate-900" data-id="element-14142">#47</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('1 247 просмотров за неделю');
      }} data-id="element-14143">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14144">
            Просмотры
          </div>
          <div className="text-lg font-bold text-slate-900" data-id="element-14145">1 247</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('Конверсия 8.3%');
      }} data-id="element-14146">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14147">
            Конверсия
          </div>
          <div className="text-lg font-bold text-emerald-600" data-id="element-14148">8.3%</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors bg-emerald-50/50 border-emerald-200" onClick={() => showToast('Доход с продвижения за месяц')} data-id="element-14149">
          <div className="text-xs text-emerald-600 mb-1 font-medium" data-id="element-14150">
            Доход с рекламы
          </div>
          <div className="text-lg font-bold text-emerald-700" data-id="element-14151">
            {totalAdIncome.toLocaleString('ru-RU')} ₽
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors bg-indigo-50/50 border-indigo-200" onClick={() => showToast('Заказы с продвижения за месяц')} data-id="element-14152">
          <div className="text-xs text-indigo-600 mb-1 font-medium" data-id="element-14153">
            Заказы с рекламы
          </div>
          <div className="text-lg font-bold text-indigo-700" data-id="element-14154">
            {totalAdOrders}
          </div>
        </Card>
      </div>

      {/* Section 2: CTA Block — How to increase income */}
      <Card padding="md" className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-none relative overflow-hidden" data-id="element-14155">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl" data-id="element-14156"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-14157">
          <div className="flex-1" data-id="element-14158">
            <div className="flex items-center gap-2 mb-2" data-id="element-14159">
              <Sparkles className="w-5 h-5 text-amber-300" data-id="element-14160" />
              <h3 className="text-xl font-bold" data-id="element-14161">Как увеличить доход</h3>
            </div>
            <p className="text-indigo-100 text-sm max-w-lg" data-id="element-14162">
              Перейдите на тариф <strong data-id="element-14163">PRO</strong> и получите приоритет в
              каталоге. По статистике, PRO-астрологи получают{' '}
              <strong data-id="element-14164">на 40% больше заказов</strong>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0" data-id="element-14165">
            <Button variant="primary" className="bg-white text-indigo-700 hover:bg-indigo-50 border-none shadow-lg" onClick={() => {
            setSelectedTariff('pro');
            setShowTariffModal(true);
          }} data-id="element-14166">
              Перейти на PRO
            </Button>
            <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => showToast('Запуск рекламной кампании')} data-id="element-14167">
              Включить рекламу
            </Button>
          </div>
        </div>
      </Card>

      {/* Section 3: Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-14168">
        {[{
        icon: Rocket,
        label: 'Запустить рекламу',
        color: 'text-indigo-600 bg-indigo-50',
        action: () => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }
      }, {
        icon: ArrowUpCircle,
        label: 'Поднять профиль',
        color: 'text-amber-600 bg-amber-50',
        action: () => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }
      }, {
        icon: Mail,
        label: 'Включить рассылку',
        color: 'text-emerald-600 bg-emerald-50',
        action: () => showToast('Рассылка включена — клиенты получат уведомление о ваших услугах')
      }, {
        icon: Crown,
        label: 'Улучшить тариф',
        color: 'text-purple-600 bg-purple-50',
        action: () => {
          setSelectedTariff('pro');
          setShowTariffModal(true);
        }
      }].map((item, i) => <button key={i} onClick={item.action} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-sm transition-all text-center group" data-id="element-14169">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color} group-hover:scale-110 transition-transform`} data-id="element-14170">
              <item.icon className="w-5 h-5" data-id="element-14171" />
            </div>
            <span className="text-sm font-medium text-slate-700" data-id="element-14172">
              {item.label}
            </span>
          </button>)}
      </div>

      {/* Section 4: Ad Orders */}
      <Card padding="none" className="overflow-hidden" data-id="element-14173">
        <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-14174">
          <div data-id="element-14175">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-14176">
              Мои рекламные заказы
            </h3>
            <button onClick={() => setShowHowItWorks(!showHowItWorks)} className="text-xs text-indigo-600 font-medium hover:underline mt-0.5" data-id="element-14177">
              {showHowItWorks ? 'Скрыть справку' : 'Как работает реклама?'}
            </button>
          </div>
          <Button variant="primary" size="sm" icon={<Megaphone className="w-4 h-4" data-id="element-14179" />} onClick={() => setShowNewAdModal(true)} data-id="element-14178">
            Новый заказ
          </Button>
        </div>
        {showHowItWorks && <div className="px-4 sm:px-5 py-4 bg-indigo-50/50 border-b border-indigo-100 animate-in fade-in slide-in-from-top-2" data-id="element-14180">
            <h4 className="font-bold text-indigo-900 text-sm mb-2" data-id="element-14181">
              Как работает реклама на платформе
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-indigo-800" data-id="element-14182">
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14183">
                <div className="font-bold mb-1" data-id="element-14184">1. Выберите формат</div>
                <p className="text-indigo-600" data-id="element-14185">
                  Баннер, закреп в каталоге или рассылка — каждый формат
                  работает по-разному
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14186">
                <div className="font-bold mb-1" data-id="element-14187">2. Оплатите с баланса</div>
                <p className="text-indigo-600" data-id="element-14188">
                  Деньги списываются с рекламного бюджета. Пополните заранее.
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14189">
                <div className="font-bold mb-1" data-id="element-14190">3. Получайте заказы</div>
                <p className="text-indigo-600" data-id="element-14191">
                  Отслеживайте просмотры, клики и заказы в реальном времени
                </p>
              </div>
            </div>
          </div>}

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto" data-id="element-14192">
          <table className="w-full text-left text-sm" data-id="element-14193">
            <thead className="bg-slate-50/50 border-b border-slate-100 text-slate-500" data-id="element-14194">
              <tr data-id="element-14195">
                <th className="px-5 py-3 font-semibold" data-id="element-14196">Услуга</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14197">Тип</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14198">Статус</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14199">Период</th>
                <th className="px-5 py-3 font-semibold text-right" data-id="element-14200">
                  Стоимость
                </th>
                <th className="px-5 py-3 font-semibold text-right" data-id="element-14201">
                  Результат
                </th>
                <th className="px-5 py-3 w-10" data-id="element-14202"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-14203">
              {adOrders.map(order => {
              const isExpanded = expandedOrderId === order.id;
              const statusConfig = getStatusConfig(order.status);
              return <Fragment key={order.id} data-id="element-14204">
                    <tr className={`hover:bg-slate-50/80 transition-colors cursor-pointer ${isExpanded ? 'bg-slate-50/80' : ''}`} onClick={() => setExpandedOrderId(isExpanded ? null : order.id)} data-id="element-14205">
                      <td className="px-5 py-3" data-id="element-14206">
                        <div className="font-bold text-slate-900" data-id="element-14207">
                          {order.name}
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-14208">
                          {order.id}
                        </div>
                      </td>
                      <td className="px-5 py-3" data-id="element-14209">
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md" data-id="element-14210">
                          {getTypeLabel(order.type)}
                        </span>
                      </td>
                      <td className="px-5 py-3" data-id="element-14211">
                        <Badge variant={statusConfig.variant} size="sm" data-id="element-14212">
                          {statusConfig.label}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-slate-600 text-xs" data-id="element-14213">
                        {order.endDate ? `${order.startDate} — ${order.endDate}` : `С ${order.startDate}`}
                      </td>
                      <td className="px-5 py-3 text-right font-bold text-slate-900" data-id="element-14214">
                        {order.price.toLocaleString('ru-RU')} ₽
                      </td>
                      <td className="px-5 py-3 text-right" data-id="element-14215">
                        <span className="text-xs text-slate-500" data-id="element-14216">
                          {order.clicks} кликов / {order.orders} заказов
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-400" data-id="element-14217">
                        {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-14218" /> : <ChevronDown className="w-5 h-5" data-id="element-14219" />}
                      </td>
                    </tr>

                    {isExpanded && <tr className="bg-slate-50/50" data-id="element-14220">
                        <td colSpan={7} className="px-5 py-4" data-id="element-14221">
                          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm animate-in fade-in slide-in-from-top-2" data-id="element-14222">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-5" data-id="element-14223">
                              <div data-id="element-14224">
                                <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14225">
                                  <Eye className="w-3 h-3" data-id="element-14226" /> Просмотры
                                </div>
                                <div className="text-xl font-bold text-slate-900" data-id="element-14227">
                                  {order.views.toLocaleString('ru-RU')}
                                </div>
                              </div>
                              <div data-id="element-14228">
                                <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14229">
                                  <MousePointerClick className="w-3 h-3" data-id="element-14230" />{' '}
                                  Клики
                                </div>
                                <div className="text-xl font-bold text-slate-900" data-id="element-14231">
                                  {order.clicks.toLocaleString('ru-RU')}
                                </div>
                              </div>
                              <div data-id="element-14232">
                                <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14233">
                                  <ShoppingBag className="w-3 h-3" data-id="element-14234" /> Заказы
                                </div>
                                <div className="text-xl font-bold text-indigo-600" data-id="element-14235">
                                  {order.orders}
                                </div>
                              </div>
                              <div data-id="element-14236">
                                <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14237">
                                  <DollarSign className="w-3 h-3" data-id="element-14238" /> Доход
                                </div>
                                <div className="text-xl font-bold text-emerald-600" data-id="element-14239">
                                  {order.income.toLocaleString('ru-RU')} ₽
                                </div>
                              </div>
                            </div>

                            {order.views > 0 && <div className="text-xs text-slate-500 mb-4 bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-14240">
                                CTR:{' '}
                                <strong className="text-slate-700" data-id="element-14241">
                                  {(order.clicks / order.views * 100).toFixed(1)}
                                  %
                                </strong>{' '}
                                · Конверсия:{' '}
                                <strong className="text-slate-700" data-id="element-14242">
                                  {order.clicks > 0 ? (order.orders / order.clicks * 100).toFixed(1) : 0}
                                  %
                                </strong>{' '}
                                · ROI:{' '}
                                <strong className={order.income > order.price ? 'text-emerald-600' : 'text-rose-600'} data-id="element-14243">
                                  {order.price > 0 ? ((order.income - order.price) / order.price * 100).toFixed(0) : 0}
                                  %
                                </strong>
                              </div>}

                            <div className="flex gap-2" data-id="element-14244">
                              <Button variant="primary" size="sm" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14246" />} onClick={e => {
                          e.stopPropagation();
                          handleRepeatOrder(order);
                        }} data-id="element-14245">
                                Повторить
                              </Button>
                              {order.status === 'active' && <Button variant="outline" size="sm" icon={<CalendarPlus className="w-3.5 h-3.5" data-id="element-14248" />} onClick={e => {
                          e.stopPropagation();
                          handleExtendOrder(order);
                        }} data-id="element-14247">
                                  Продлить
                                </Button>}
                            </div>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-slate-100" data-id="element-14249">
          {adOrders.map(order => {
          const isExpanded = expandedOrderId === order.id;
          const statusConfig = getStatusConfig(order.status);
          return <div key={order.id} data-id="element-14250">
                <div className={`p-4 cursor-pointer ${isExpanded ? 'bg-slate-50' : ''}`} onClick={() => setExpandedOrderId(isExpanded ? null : order.id)} data-id="element-14251">
                  <div className="flex justify-between items-start mb-2" data-id="element-14252">
                    <div data-id="element-14253">
                      <div className="font-bold text-slate-900 text-sm" data-id="element-14254">
                        {order.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5" data-id="element-14255">
                        {getTypeLabel(order.type)} · {order.id}
                      </div>
                    </div>
                    <Badge variant={statusConfig.variant} size="sm" data-id="element-14256">
                      {statusConfig.label}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-14257">
                    <span className="text-xs text-slate-500" data-id="element-14258">
                      {order.startDate}
                      {order.endDate ? ` — ${order.endDate}` : ''}
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-14259">
                      {order.price.toLocaleString('ru-RU')} ₽
                    </span>
                  </div>
                </div>

                {isExpanded && <div className="p-4 bg-slate-50 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-14260">
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-14261">
                      <div data-id="element-14262">
                        <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14263">
                          Просмотры
                        </div>
                        <div className="font-bold text-slate-900" data-id="element-14264">
                          {order.views.toLocaleString('ru-RU')}
                        </div>
                      </div>
                      <div data-id="element-14265">
                        <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14266">
                          Клики
                        </div>
                        <div className="font-bold text-slate-900" data-id="element-14267">
                          {order.clicks.toLocaleString('ru-RU')}
                        </div>
                      </div>
                      <div data-id="element-14268">
                        <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14269">
                          Заказы
                        </div>
                        <div className="font-bold text-indigo-600" data-id="element-14270">
                          {order.orders}
                        </div>
                      </div>
                      <div data-id="element-14271">
                        <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14272">
                          Доход
                        </div>
                        <div className="font-bold text-emerald-600" data-id="element-14273">
                          {order.income.toLocaleString('ru-RU')} ₽
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2" data-id="element-14274">
                      <Button variant="primary" size="sm" className="flex-1" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14276" />} onClick={e => {
                  e.stopPropagation();
                  handleRepeatOrder(order);
                }} data-id="element-14275">
                        Повторить
                      </Button>
                      {order.status === 'active' && <Button variant="outline" size="sm" className="flex-1" icon={<CalendarPlus className="w-3.5 h-3.5" data-id="element-14278" />} onClick={e => {
                  e.stopPropagation();
                  handleExtendOrder(order);
                }} data-id="element-14277">
                          Продлить
                        </Button>}
                    </div>
                  </div>}
              </div>;
        })}
        </div>
      </Card>

      {/* Section 5: Tariff Plans */}
      <h3 className="text-lg font-bold text-slate-900 mt-2" data-id="element-14279">Тарифные планы</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-14280">
        {/* Basic */}
        <Card padding="md" className="flex flex-col border-slate-200" data-id="element-14281">
          <div className="mb-4" data-id="element-14282">
            <h4 className="text-lg font-bold text-slate-900 mb-1" data-id="element-14283">Базовый</h4>
            <div className="text-2xl font-bold text-slate-900" data-id="element-14284">Бесплатно</div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-14285">~5-10 заказов/мес</p>
          </div>
          <ul className="space-y-2 mb-6 flex-1" data-id="element-14286">
            {['Профиль в каталоге', 'До 3 услуг', 'Стандартная позиция'].map(f => <li key={f} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-14287">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-14288" />{' '}
                  {f}
                </li>)}
          </ul>
          <div className="py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-center text-sm font-medium text-slate-600 flex items-center justify-center gap-2" data-id="element-14289">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-14290" /> Ваш текущий
            план
          </div>
        </Card>

        {/* PRO */}
        <Card padding="md" className="flex flex-col border-indigo-200 shadow-md ring-1 ring-indigo-100 relative" data-id="element-14291">
          <div className="absolute top-0 right-0 bg-indigo-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg rounded-tr-xl uppercase tracking-wider" data-id="element-14292">
            Популярный
          </div>
          <div className="mb-4" data-id="element-14293">
            <h4 className="text-lg font-bold text-indigo-700 mb-1 flex items-center gap-2" data-id="element-14294">
              PRO <ShieldCheck className="w-5 h-5" data-id="element-14295" />
            </h4>
            <div className="flex items-baseline gap-1" data-id="element-14296">
              <span className="text-2xl font-bold text-slate-900" data-id="element-14297">3 000 ₽</span>
              <span className="text-slate-500 text-sm" data-id="element-14298">/ мес</span>
            </div>
            <p className="text-xs text-emerald-600 font-medium mt-1" data-id="element-14299">
              ~20-35 заказов/мес (+40%)
            </p>
          </div>
          <ul className="space-y-2 mb-6 flex-1" data-id="element-14300">
            {['Бейдж PRO', 'Приоритет в поиске', 'До 10 услуг', 'Расширенная аналитика', 'Поддержка в чате'].map(f => <li key={f} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-14301">
                <Check className="w-4 h-4 text-indigo-500 flex-shrink-0" data-id="element-14302" /> {f}
              </li>)}
          </ul>
          <Button variant="primary" fullWidth onClick={() => {
          setSelectedTariff('pro');
          setShowTariffModal(true);
        }} data-id="element-14303">
            Перейти на PRO
          </Button>
        </Card>

        {/* TOP */}
        <Card padding="md" className="flex flex-col bg-gradient-to-b from-amber-50/50 to-white border-amber-200 relative" data-id="element-14304">
          <div className="mb-4" data-id="element-14305">
            <h4 className="text-lg font-bold text-amber-700 mb-1 flex items-center gap-2" data-id="element-14306">
              TOP <Crown className="w-5 h-5" data-id="element-14307" />
            </h4>
            <div className="flex items-baseline gap-1" data-id="element-14308">
              <span className="text-2xl font-bold text-slate-900" data-id="element-14309">7 000 ₽</span>
              <span className="text-slate-500 text-sm" data-id="element-14310">/ мес</span>
            </div>
            <p className="text-xs text-emerald-600 font-medium mt-1" data-id="element-14311">
              ~50-80 заказов/мес (+120%)
            </p>
          </div>
          <ul className="space-y-2 mb-6 flex-1" data-id="element-14312">
            {['Всё из PRO', 'Бейдж TOP Астролог', 'Закреп вверху каталога', 'Блок на главной', 'Безлимит услуг', 'Персональный менеджер'].map(f => <li key={f} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-14313">
                <Check className="w-4 h-4 text-amber-500 flex-shrink-0" data-id="element-14314" /> {f}
              </li>)}
          </ul>
          <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" fullWidth onClick={() => {
          setSelectedTariff('top');
          setShowTariffModal(true);
        }} data-id="element-14315">
            Перейти на TOP
          </Button>
        </Card>
      </div>

      {/* Section 6: Active Placements */}
      <Card padding="md" data-id="element-14316">
        <div className="flex justify-between items-center mb-4" data-id="element-14317">
          <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-14318">
            <Target className="w-5 h-5 text-amber-500" data-id="element-14319" /> Где вы размещены
          </h3>
          <Button variant="outline" size="sm" onClick={() => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }} data-id="element-14320">
            Купить размещение
          </Button>
        </div>

        {activePlacements.length > 0 ? <div className="space-y-3" data-id="element-14321">
            {activePlacements.map((p, i) => <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border border-amber-100 bg-amber-50/30" data-id="element-14322">
                <div className="flex items-center gap-3" data-id="element-14323">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center" data-id="element-14324">
                    <Target className="w-4 h-4 text-amber-600" data-id="element-14325" />
                  </div>
                  <div data-id="element-14326">
                    <div className="font-bold text-slate-900 text-sm" data-id="element-14327">
                      {p.type}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-14328">{p.category}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm" data-id="element-14329">
                  <div className="flex items-center gap-1 text-slate-600" data-id="element-14330">
                    <Clock className="w-3.5 h-3.5 text-amber-500" data-id="element-14331" />
                    <span data-id="element-14332">{p.daysLeft} дн.</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-600" data-id="element-14333">
                    <Eye className="w-3.5 h-3.5 text-indigo-500" data-id="element-14334" />
                    <span data-id="element-14335">{p.views.toLocaleString('ru-RU')}</span>
                  </div>
                  <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50 h-8 px-2" onClick={() => {
              setShowPinModal(true);
              setSelectedPinCategory(p.category);
            }} data-id="element-14336">
                    Продлить
                  </Button>
                </div>
              </div>)}
          </div> : <div className="text-center py-8 text-slate-500" data-id="element-14337">
            <Target className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-14338" />
            <p className="text-sm" data-id="element-14339">У вас нет активных размещений</p>
          </div>}
      </Card>

      {/* Section 7: Simple Analytics */}
      <Card padding="md" data-id="element-14340">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-14341">
          <BarChart3 className="w-5 h-5 text-indigo-500" data-id="element-14342" /> Эффективность за
          месяц
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-14343">
          <div className="text-center p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-14344">
            <Eye className="w-5 h-5 text-slate-400 mx-auto mb-2" data-id="element-14345" />
            <div className="text-2xl font-bold text-slate-900" data-id="element-14346">1 247</div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-14347">Просмотры</div>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-14348">
            <MousePointerClick className="w-5 h-5 text-indigo-400 mx-auto mb-2" data-id="element-14349" />
            <div className="text-2xl font-bold text-slate-900" data-id="element-14350">620</div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-14351">Клики</div>
          </div>
          <div className="text-center p-4 bg-indigo-50 rounded-xl border border-indigo-100" data-id="element-14352">
            <ShoppingBag className="w-5 h-5 text-indigo-500 mx-auto mb-2" data-id="element-14353" />
            <div className="text-2xl font-bold text-indigo-700" data-id="element-14354">39</div>
            <div className="text-xs text-indigo-600 mt-1" data-id="element-14355">Заказы</div>
          </div>
          <div className="text-center p-4 bg-emerald-50 rounded-xl border border-emerald-100" data-id="element-14356">
            <DollarSign className="w-5 h-5 text-emerald-500 mx-auto mb-2" data-id="element-14357" />
            <div className="text-2xl font-bold text-emerald-700" data-id="element-14358">117 000 ₽</div>
            <div className="text-xs text-emerald-600 mt-1" data-id="element-14359">Доход</div>
          </div>
        </div>
      </Card>

      {/* Tariff Upgrade Modal */}
      {showTariffModal && selectedTariff && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-14360">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-14361">
            <div className="flex justify-between items-center p-5 border-b border-slate-100" data-id="element-14362">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-14363">
                Переход на {selectedTariff === 'pro' ? 'PRO' : 'TOP'}
              </h3>
              <button onClick={() => setShowTariffModal(false)} className="text-slate-400 hover:text-slate-600" data-id="element-14364">
                <X className="w-5 h-5" data-id="element-14365" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-14366">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-14367">
                <div className="flex justify-between text-sm mb-3" data-id="element-14368">
                  <span className="text-slate-500" data-id="element-14369">Тариф</span>
                  <span className="font-bold text-slate-900" data-id="element-14370">
                    {selectedTariff === 'pro' ? 'PRO' : 'TOP'}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-3" data-id="element-14371">
                  <span className="text-slate-500" data-id="element-14372">Стоимость</span>
                  <span className="font-bold text-slate-900" data-id="element-14373">
                    {selectedTariff === 'pro' ? '3 000' : '7 000'} ₽ / мес
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-3" data-id="element-14374">
                  <span className="text-slate-500" data-id="element-14375">Ожидаемый рост заказов</span>
                  <span className="font-bold text-emerald-600" data-id="element-14376">
                    {selectedTariff === 'pro' ? '+40%' : '+120%'}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-3 border-t border-slate-200" data-id="element-14377">
                  <span className="font-bold text-slate-900" data-id="element-14378">
                    К оплате сейчас
                  </span>
                  <span className="text-xl font-bold text-indigo-600" data-id="element-14379">
                    {selectedTariff === 'pro' ? '3 000' : '7 000'} ₽
                  </span>
                </div>
              </div>

              <p className="text-xs text-slate-500" data-id="element-14380">
                Оплата будет списана с вашего баланса. Тариф активируется
                мгновенно. Отмена возможна в любое время.
              </p>

              <div className="flex gap-3" data-id="element-14381">
                <Button variant="outline" fullWidth onClick={() => setShowTariffModal(false)} data-id="element-14382">
                  Отмена
                </Button>
                <Button variant="primary" fullWidth onClick={() => handleUpgradeTariff(selectedTariff)} data-id="element-14383">
                  Оплатить и активировать
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {/* New Ad Order Modal */}
      {showNewAdModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-14384">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto" data-id="element-14385">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 sticky top-0 bg-white z-10" data-id="element-14386">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-14387">
                Создать рекламу
              </h3>
              <button onClick={() => {
            setShowNewAdModal(false);
            setSelectedAdSlot(null);
          }} className="text-slate-400 hover:text-slate-600" data-id="element-14388">
                <X className="w-5 h-5" data-id="element-14389" />
              </button>
            </div>
            <div className="p-5 space-y-4" data-id="element-14390">
              <p className="text-sm text-slate-600" data-id="element-14391">
                Выберите рекламный слот. Каждый формат показывается в разных
                местах сайта.
              </p>

              {[{
            id: 'banner-main',
            name: 'Баннер на главной',
            where: 'Главная страница сайта, блок «Рекомендуемые»',
            price: '5 000 ₽ / неделя',
            reach: '~15 000 показов',
            requirements: 'Изображение 1200×400px, до 50 символов текста',
            icon: LayoutGrid,
            activeCls: 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500',
            iconCls: 'bg-indigo-100 text-indigo-600'
          }, {
            id: 'banner-catalog',
            name: 'Баннер в каталоге',
            where: 'Страница каталога астрологов, верхняя позиция',
            price: '3 500 ₽ / неделя',
            reach: '~8 000 показов',
            requirements: 'Изображение 800×200px, до 40 символов текста',
            icon: Target,
            activeCls: 'border-amber-500 bg-amber-50/50 ring-1 ring-amber-500',
            iconCls: 'bg-amber-100 text-amber-600'
          }, {
            id: 'pin-category',
            name: 'Закреп в категории',
            where: 'Первые 3 позиции в выбранной категории услуг',
            price: 'от 2 000 ₽ / неделя',
            reach: '~5 000 показов',
            requirements: 'Ваш профиль должен быть заполнен на 80%+',
            icon: Sparkles,
            activeCls: 'border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500',
            iconCls: 'bg-emerald-100 text-emerald-600'
          }, {
            id: 'email-blast',
            name: 'Рассылка по базе',
            where: 'Email-рассылка по подписчикам категории',
            price: '3 000 ₽ / разовая',
            reach: '~10 000 получателей',
            requirements: 'Текст до 200 символов, ссылка на профиль',
            icon: Mail,
            activeCls: 'border-purple-500 bg-purple-50/50 ring-1 ring-purple-500',
            iconCls: 'bg-purple-100 text-purple-600'
          }].map(slot => <button key={slot.id} onClick={() => setSelectedAdSlot(selectedAdSlot === slot.id ? null : slot.id)} className={`w-full text-left p-4 rounded-xl border transition-all ${selectedAdSlot === slot.id ? slot.activeCls : 'border-slate-200 hover:border-slate-300'}`} data-id="element-14392">
                  <div className="flex items-start gap-3" data-id="element-14393">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${slot.iconCls}`} data-id="element-14394">
                      <slot.icon className="w-5 h-5" data-id="element-14395" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-14396">
                      <div className="flex items-center justify-between gap-2" data-id="element-14397">
                        <span className="font-bold text-slate-900 text-sm" data-id="element-14398">
                          {slot.name}
                        </span>
                        <span className="text-xs font-bold text-indigo-600 whitespace-nowrap" data-id="element-14399">
                          {slot.price}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-14400">
                        {slot.where}
                      </p>
                      {selectedAdSlot === slot.id && <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 animate-in fade-in" data-id="element-14401">
                          <div className="flex items-center gap-2 text-xs" data-id="element-14402">
                            <Eye className="w-3.5 h-3.5 text-slate-400" data-id="element-14403" />
                            <span className="text-slate-600" data-id="element-14404">
                              Охват:{' '}
                              <strong className="text-slate-900" data-id="element-14405">
                                {slot.reach}
                              </strong>
                            </span>
                          </div>
                          <div className="flex items-start gap-2 text-xs" data-id="element-14406">
                            <AlertCircle className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-14407" />
                            <span className="text-slate-600" data-id="element-14408">
                              Требования:{' '}
                              <strong className="text-slate-900" data-id="element-14409">
                                {slot.requirements}
                              </strong>
                            </span>
                          </div>
                        </div>}
                    </div>
                  </div>
                </button>)}

              {selectedAdSlot && <div className="flex gap-3 pt-2" data-id="element-14410">
                  <Button variant="outline" fullWidth onClick={() => {
              setShowNewAdModal(false);
              setSelectedAdSlot(null);
            }} data-id="element-14411">
                    Отмена
                  </Button>
                  <Button variant="primary" fullWidth onClick={() => {
              if (selectedAdSlot === 'pin-category') {
                setShowNewAdModal(false);
                setSelectedAdSlot(null);
                setShowPinModal(true);
                setSelectedPinCategory('Натальная карта');
              } else {
                setShowNewAdModal(false);
                setSelectedAdSlot(null);
                showToast('Рекламный заказ создан и отправлен на модерацию ✓');
              }
            }} data-id="element-14412">
                    {selectedAdSlot === 'pin-category' ? 'Выбрать категорию' : 'Оформить заказ'}
                  </Button>
                </div>}
            </div>
          </div>
        </div>}

      {/* Pin Purchase Modal */}
      {showPinModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-14413">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-14414">
            <div className="flex justify-between items-center p-5 border-b border-slate-100" data-id="element-14415">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-14416">
                Купить размещение
              </h3>
              <button onClick={() => setShowPinModal(false)} className="text-slate-400 hover:text-slate-600" data-id="element-14417">
                <X className="w-5 h-5" data-id="element-14418" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-14419">
              <div data-id="element-14420">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-14421">
                  Категория
                </label>
                <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={selectedPinCategory} onChange={e => setSelectedPinCategory(e.target.value)} data-id="element-14422">
                  <option value="Натальная карта" data-id="element-14423">Натальная карта</option>
                  <option value="Совместимость" data-id="element-14424">Совместимость</option>
                  <option value="Соляр" data-id="element-14425">Соляр на год</option>
                  <option value="Транзиты" data-id="element-14426">Транзиты</option>
                </select>
              </div>

              <div data-id="element-14427">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-14428">
                  Длительность
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-14429">
                  {[{
                value: '1week' as const,
                label: '1 неделя',
                price: '2 000 ₽'
              }, {
                value: '2weeks' as const,
                label: '2 недели',
                price: '3 500 ₽'
              }, {
                value: '1month' as const,
                label: '1 месяц',
                price: '6 000 ₽'
              }].map(opt => <button key={opt.value} onClick={() => setSelectedPinDuration(opt.value)} className={`p-3 rounded-xl border text-center transition-all ${selectedPinDuration === opt.value ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-indigo-300'}`} data-id="element-14430">
                      <div className="text-sm font-bold text-slate-900" data-id="element-14431">
                        {opt.label}
                      </div>
                      <div className="text-xs text-indigo-600 font-medium mt-1" data-id="element-14432">
                        {opt.price}
                      </div>
                    </button>)}
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-14433">
                <div className="flex justify-between text-sm mb-2" data-id="element-14434">
                  <span className="text-slate-500" data-id="element-14435">Категория</span>
                  <span className="font-medium text-slate-900" data-id="element-14436">
                    {selectedPinCategory}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-slate-200" data-id="element-14437">
                  <span className="font-bold text-slate-900" data-id="element-14438">К оплате</span>
                  <span className="text-xl font-bold text-indigo-600" data-id="element-14439">
                    {selectedPinDuration === '1week' ? '2 000' : selectedPinDuration === '2weeks' ? '3 500' : '6 000'}{' '}
                    ₽
                  </span>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-14440">
                <Button variant="outline" fullWidth onClick={() => setShowPinModal(false)} data-id="element-14441">
                  Отмена
                </Button>
                <Button variant="primary" fullWidth onClick={handleBuyPin} data-id="element-14442">
                  Оплатить
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}